import * as React from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useBrandingAccent } from "@/hooks/useBrandingAccent";

/**
 * Loads the active company for the URL param :companyId. Exposes:
 *   company, role, capabilities (Set), entitlements, catalog, refresh(), has(cap)
 * Also lists all companies the user belongs to (for the sidebar switcher).
 */
const CompanyContext = React.createContext(null);

export function CompanyProvider({ children }) {
  const { companyId } = useParams();
  const [companies, setCompanies] = React.useState([]);
  const [company, setCompany] = React.useState(null);
  const [role, setRole] = React.useState(null);
  const [capabilities, setCapabilities] = React.useState(new Set());
  const [entitlements, setEntitlements] = React.useState(null);
  const [catalog, setCatalog] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const loadCatalog = React.useCallback(async () => {
    if (catalog) return catalog;
    const { data } = await api.get("/capabilities");
    setCatalog(data);
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listCompanies = React.useCallback(async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
      return data;
    } catch { return []; }
  }, []);

  const refresh = React.useCallback(async () => {
    if (!companyId) {
      setCompany(null); setRole(null); setCapabilities(new Set()); setEntitlements(null);
      setLoading(false);
      return;
    }
    setLoading(true); setError(null);
    try {
      const [{ data }, _cat] = await Promise.all([
        api.get(`/companies/${companyId}`),
        loadCatalog(),
      ]);
      setCompany(data.company);
      setRole(data.role);
      setCapabilities(new Set(data.capabilities || []));
      setEntitlements(data.entitlements);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId, loadCatalog]);

  React.useEffect(() => { listCompanies(); }, [listCompanies]);
  React.useEffect(() => { refresh(); }, [refresh]);

  const has = React.useCallback((cap) => capabilities.has(cap), [capabilities]);

  // Phase 15.5c — repaint document root with the org accent whenever
  // the active company gains company.manage. Non-managers keep the
  // platform default; the base tokens.css handles their palette.
  const brandingHandle = useBrandingAccent(companyId, {
    enabled: !!company && capabilities.has("company.manage"),
  });

  const value = {
    companyId, companies, company, role, capabilities, entitlements,
    catalog, loading, error, refresh, listCompanies, has,
    setEntitlements, // allow local mutation after PATCH
    branding: brandingHandle,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany() {
  const v = React.useContext(CompanyContext);
  if (!v) throw new Error("useCompany must be used inside <CompanyProvider>");
  return v;
}
